import type React from 'react';
import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import { ITEM_SLOT_LABELS, type ItemEquipmentSlot } from '../../../../shared/equipmentSlots';
import type { InventoryItem } from '../../../../shared/CharacterTypes';
import type { ItemData } from '../../components/ItemKit';
import { ACCENT, COLORS, FONTS, INK, RARITY } from '../../theme';

export interface EquipmentSlotDefinition {
  id: ItemEquipmentSlot;
  label: string;
  /** `center` sits under the character preview (the Flame and the Lantern). */
  side: 'left' | 'right' | 'center';
}

// Labels come from ITEM_SLOT_LABELS (keys are storage, labels presentation).
// Lane BD (D46): `shoes` reads Trail and sits last on the right — it styles
// the ghost's light trail rather than dressing a body part; Hands moved left
// to keep four slots a side. Lane BK (B26): the Lantern is a ninth slot, so it
// sits centred under the preview — the light the ghost carries — and the
// four-a-side columns stay as they were.
export const EQUIPMENT_SLOTS = [
  { id: 'hat', label: ITEM_SLOT_LABELS.hat, side: 'left' },
  { id: 'top', label: ITEM_SLOT_LABELS.top, side: 'left' },
  { id: 'bottom', label: ITEM_SLOT_LABELS.bottom, side: 'left' },
  { id: 'gloves', label: ITEM_SLOT_LABELS.gloves, side: 'left' },
  { id: 'weapon', label: ITEM_SLOT_LABELS.weapon, side: 'right' },
  { id: 'cape', label: ITEM_SLOT_LABELS.cape, side: 'right' },
  { id: 'accessory', label: ITEM_SLOT_LABELS.accessory, side: 'right' },
  { id: 'shoes', label: ITEM_SLOT_LABELS.shoes, side: 'right' },
  { id: 'flame', label: ITEM_SLOT_LABELS.flame, side: 'center' },
  { id: 'lantern', label: ITEM_SLOT_LABELS.lantern, side: 'center' },
] as const satisfies readonly EquipmentSlotDefinition[];

const ITEM_DRAG_MIME = 'application/x-helix-item';

export interface DraggedInventoryItem {
  itemId: string;
  name: string;
  equipmentSlot: ItemEquipmentSlot | null;
}

export type EquipmentDropState = 'idle' | 'available' | 'valid' | 'invalid';
export type CharacterTab = 'equipment' | 'stats';
type CharacterCssVariables = React.CSSProperties & Record<`--character-${string}`, string>;

export const CHARACTER_THEME_STYLE: CharacterCssVariables = {
  '--character-ink-hi': INK.hi,
  '--character-ink': INK.base,
  '--character-ink-lo': INK.lo,
  '--character-well': INK.well,
  '--character-well-lo': INK.wellLo,
  '--character-brass': ACCENT.brass,
  '--character-brass-bright': ACCENT.brassBright,
  '--character-text': COLORS.textPrimary,
  '--character-text-secondary': COLORS.textSecondary,
  '--character-text-muted': COLORS.textMuted,
  '--character-border': COLORS.borderSubtle,
  '--character-success': COLORS.success,
  '--character-danger': COLORS.danger,
  '--character-font-display': FONTS.display,
  '--character-font-ui': FONTS.ui,
};

export function humanize(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w|\s\w/g, character => character.toUpperCase());
}

export function slotDefinition(slotId: ItemEquipmentSlot): EquipmentSlotDefinition {
  return EQUIPMENT_SLOTS.find(slot => slot.id === slotId)!;
}

export function itemEquipmentSlot(itemId: string): ItemEquipmentSlot | null {
  const item = ITEM_DATABASE[itemId];
  if (!item || (item.type !== 'weapon' && item.type !== 'armor')) return null;
  return item.equipmentSlot ?? null;
}

export function rarityTone(item: ItemData | null): string {
  if (!item?.rarity) return ACCENT.brass;
  return RARITY[item.rarity] ?? ACCENT.brass;
}

export function formatItemStat(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

/** Read both the typed Helix payload and the interoperable text fallback. */
export function readDraggedItemId(dataTransfer: Pick<DataTransfer, 'getData'>): string | null {
  try {
    const typed = dataTransfer.getData(ITEM_DRAG_MIME);
    if (typed) return typed;
    const plain = dataTransfer.getData('text/plain');
    return plain.startsWith('item:') ? plain.slice('item:'.length) || null : null;
  } catch {
    return null;
  }
}

export function dropToast(message: string): void {
  try {
    (globalThis as unknown as {
      __errorToast?: (level: 'error' | 'warn' | 'info', text: string) => void;
    }).__errorToast?.('info', message);
  } catch {
    // Toasts are optional in isolated UI tests and early boot.
  }
}


export function compatibleInventoryItems(
  items: readonly InventoryItem[],
  selectedSlot: ItemEquipmentSlot,
): InventoryItem[] {
  return items.filter(item => itemEquipmentSlot(item.id) === selectedSlot);
}
